import React, { useEffect, useState, useCallback } from 'react';
import { TenantList } from '../vendor/prism-components';
import { useTranslation } from '../contexts/LanguageContext';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Shared/Toast';
import SaasLicensePanel from '../components/license/SaasLicensePanel';

/**
 * TenantManagementPage -- Block 3.
 *
 * Layout: <TenantList> (links) + Detail-Panel mit Tabs (rechts).
 * Tabs: info | licenses | users | ai_credentials | links
 *
 * Backend: Platform-Admin-API /api/platform/admin/* (useApi).
 * Frueher lief die Seite ueber den toten /api/provider/*-Pfad
 * (useProviderApi) -> 404. Der Provider-Router existiert in prism-os
 * nicht; Tenants/Users liegen unter /api/platform/admin. Der frueher
 * eigene Einzel-Lizenz-PATCH existiert ebenfalls nicht mehr -- der
 * Lizenz-Tab nutzt jetzt das pro-Tool-Modell via <SaasLicensePanel>.
 */

const TABS = ['info', 'licenses', 'users', 'ai_credentials', 'links'];

export default function TenantManagementPage() {
  const { t } = useTranslation();
  const { api } = useApi();
  const { userRole } = useAuth();
  const { showToast } = useToast();
  const canManage = userRole === 'platform_admin';

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  // Create-Modal
  const [createOpen, setCreateOpen] = useState(false);

  // Edit-Tenant-Modal
  const [editTenantOpen, setEditTenantOpen] = useState(false);

  // Users-Tab
  const [tenantUsers, setTenantUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userCreateOpen, setUserCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);   // zu editierender User oder null

  // --- Loader ----------------------------------------------------------------
  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/admin/tenants');
      const list = Array.isArray(data) ? data : (data?.items || data?.tenants || []);
      setTenants(list);
      return list;
    } catch (err) {
      showToast(t('common.error') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
      return [];
    } finally {
      setLoading(false);
    }
  }, [api, showToast, t]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  // Beim Selection-Wechsel: Tab + Users zuruecksetzen
  useEffect(() => {
    if (selected) {
      setActiveTab('info');
      setTenantUsers([]);
    }
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Users eines Mandanten laden (wiederverwendbar: Tab-Wechsel + nach Anlage)
  const loadUsers = useCallback(async () => {
    if (!selected) return;
    setUsersLoading(true);
    try {
      const data = await api('/admin/users', { params: { tenant_id: selected.id } });
      setTenantUsers(Array.isArray(data) ? data : (data?.items || []));
    } catch (err) {
      showToast(t('common.error') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setUsersLoading(false);
    }
  }, [api, selected, showToast, t]);

  useEffect(() => {
    if (activeTab !== 'users' || !selected) return;
    loadUsers();
  }, [activeTab, selected, loadUsers]);

  // --- Handlers --------------------------------------------------------------
  const handleCreated = async (newTenant) => {
    setCreateOpen(false);
    const list = await loadTenants();
    const full = (list || []).find(x => x.id === newTenant?.id) || newTenant;
    if (full?.id) setSelected(full);
    showToast(t('admin.tenants.create.success'), 'success');
  };

  const handleUserCreated = async () => {
    setUserCreateOpen(false);
    await loadUsers();
    showToast(t('admin.tenants.users.create.success', 'Benutzer angelegt.'), 'success');
  };

  const handleTenantUpdated = async () => {
    setEditTenantOpen(false);
    const list = await loadTenants();
    const full = (list || []).find(x => x.id === selected?.id);
    if (full) setSelected(full);
    showToast(t('admin.tenants.edit.success', 'Mandant aktualisiert.'), 'success');
  };

  const handleUserUpdated = async () => {
    setEditUser(null);
    await loadUsers();
    showToast(t('admin.tenants.users.edit.success', 'Benutzer aktualisiert.'), 'success');
  };

  // --- Render ----------------------------------------------------------------
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* Linke Spalte: Mandantenliste */}
      <div style={{ flex: '0 0 320px' }}>
        <TenantList
          tenants={tenants}
          selected={selected}
          onSelect={setSelected}
          loading={loading}
          onCreateClick={canManage ? () => setCreateOpen(true) : undefined}
          t={t}
        />
      </div>

      {/* Rechte Spalte: Detail-Panel */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selected ? (
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 10, padding: 32, textAlign: 'center',
            color: 'var(--text-muted)', fontSize: 13,
          }}>
            {t('admin.tenants.detail_empty')}
          </div>
        ) : (
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 10, overflow: 'hidden',
          }}>
            {/* Detail-Header */}
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid var(--border)',
              background: 'var(--bg-surface)',
            }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{selected.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {selected.slug} - {selected.id}
              </div>
            </div>

            {/* Sub-Tab-Leiste */}
            <div style={{
              display: 'flex', borderBottom: '1px solid var(--border)',
              background: 'var(--bg-surface)',
            }}>
              {TABS.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '10px 16px', fontSize: 12, fontWeight: 600,
                    background: 'transparent',
                    color: activeTab === tab ? 'var(--accent-hover)' : 'var(--text-muted)',
                    border: 'none',
                    borderBottom: activeTab === tab
                      ? '2px solid var(--accent)' : '2px solid transparent',
                    cursor: 'pointer',
                  }}
                >
                  {t(`admin.tenants.tab.${tab}`)}
                </button>
              ))}
            </div>

            {/* Tab-Inhalt */}
            <div style={{ padding: 20 }}>
              {activeTab === 'info' && (
                <InfoTab tenant={selected} t={t} canManage={canManage}
                  onEdit={() => setEditTenantOpen(true)} />
              )}
              {activeTab === 'licenses' && (
                <SaasLicensePanel
                  tenantId={selected.id}
                  api={api}
                  canManage={canManage}
                  showToast={showToast}
                  t={t}
                />
              )}
              {activeTab === 'users' && (
                <UsersTab
                  users={tenantUsers}
                  loading={usersLoading}
                  t={t}
                  canManage={canManage}
                  onCreateClick={() => setUserCreateOpen(true)}
                  onEditUser={(u) => setEditUser(u)}
                />
              )}
              {activeTab === 'ai_credentials' && (
                <StubTab text={t('admin.tenants.stub.ai')} />
              )}
              {activeTab === 'links' && (
                <StubTab text={t('admin.tenants.stub.links')} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create-Modal */}
      {createOpen && (
        <CreateTenantModal
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
          api={api}
          t={t}
        />
      )}

      {/* Create-User-Modal */}
      {userCreateOpen && selected && (
        <CreateUserModal
          onClose={() => setUserCreateOpen(false)}
          onCreated={handleUserCreated}
          api={api}
          t={t}
          tenant={selected}
        />
      )}

      {/* Edit-Tenant-Modal (auch fuer interne Mandanten) */}
      {editTenantOpen && selected && (
        <EditTenantModal
          onClose={() => setEditTenantOpen(false)}
          onUpdated={handleTenantUpdated}
          api={api}
          t={t}
          tenant={selected}
        />
      )}

      {/* Edit-User-Modal (Rolle/Status + PRISM-OS-Zugriff) */}
      {editUser && (
        <EditUserModal
          onClose={() => setEditUser(null)}
          onUpdated={handleUserUpdated}
          api={api}
          t={t}
          user={editUser}
        />
      )}
    </div>
  );
}

// --- Tab-Komponenten ---------------------------------------------------------

function InfoTab({ tenant, t, canManage, onEdit }) {
  const rows = [
    { label: t('admin.tenants.info.name'),               value: tenant.name },
    { label: t('admin.tenants.info.slug'),               value: tenant.slug },
    { label: t('admin.tenants.info.status'),             value: tenant.status },
    { label: t('admin.tenants.info.tenant_type', 'Typ'), value: tenant.tenant_type || '-' },
    { label: t('admin.tenants.info.tier', 'Tarif'),      value: tenant.tier || '-' },
    { label: t('admin.tenants.info.contact'),            value: tenant.contact_email || '-' },
    { label: t('admin.tenants.info.created_at'),         value: tenant.created_at || '-' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {canManage && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={onEdit}>
            {t('admin.tenants.edit.button', 'Mandant bearbeiten')}
          </button>
        </div>
      )}
      {rows.map(r => (
        <div key={r.label} style={{ display: 'flex', gap: 16, fontSize: 13 }}>
          <div style={{ minWidth: 160, color: 'var(--text-muted)', fontWeight: 600 }}>
            {r.label}
          </div>
          <div style={{ color: 'var(--text)' }}>{r.value}</div>
        </div>
      ))}
    </div>
  );
}

function UsersTab({ users, loading, t, canManage, onCreateClick, onEditUser }) {
  const header = canManage ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
      <button type="button" className="btn btn-primary btn-sm" onClick={onCreateClick}>
        {t('admin.tenants.users.create.button', 'Benutzer anlegen')}
      </button>
    </div>
  ) : null;

  if (loading) {
    return (
      <div>
        {header}
        <div style={{ color: 'var(--text-muted)' }}>{t('common.loading')}</div>
      </div>
    );
  }
  if (!users || users.length === 0) {
    return (
      <div>
        {header}
        <div style={{ color: 'var(--text-muted)' }}>{t('admin.tenants.users.empty')}</div>
      </div>
    );
  }
  return (
    <div>
      {header}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ background: 'var(--bg-surface)' }}>
          <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
            {t('admin.tenants.users.name')}
          </th>
          <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
            {t('admin.tenants.users.email')}
          </th>
          <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
            {t('admin.tenants.users.role')}
          </th>
          <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
            {t('admin.tenants.users.status')}
          </th>
          {canManage && (
            <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
              {t('admin.tenants.users.actions', 'Aktion')}
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {users.map(u => (
          <tr key={u.id || u.email}>
            <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
              {u.display_name || u.username || '-'}
            </td>
            <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
              {u.email || u.username}
            </td>
            <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
              {u.platform_role || u.role || '-'}
            </td>
            <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
              {u.is_active === false ? 'inactive' : (u.status || 'active')}
            </td>
            {canManage && (
              <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => onEditUser(u)}>
                  {t('admin.tenants.users.edit.button', 'Bearbeiten')}
                </button>
              </td>
            )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StubTab({ text }) {
  return (
    <div style={{
      padding: 24, textAlign: 'center', color: 'var(--text-muted)',
      fontSize: 13, fontStyle: 'italic',
    }}>
      {text}
    </div>
  );
}

// --- Create-Tenant-Modal -----------------------------------------------------

function CreateTenantModal({ onClose, onCreated, api, t }) {
  const [form, setForm] = useState({
    name: '', slug: '', status: 'trial', tenant_type: 'external', tier: 'professional',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (key) => (e) => {
    const value = e.target.value;
    setForm(f => {
      const next = { ...f, [key]: value };
      // Auto-suggest slug aus name (nur solange slug leer oder bisher automatisch)
      if (key === 'name' && (!f.slug || f.slug === slugify(f.name))) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const created = await api('/admin/tenants', { method: 'POST', body: form });
      onCreated(created);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, width: '90%', maxWidth: 480, padding: 28,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>
          {t('admin.tenants.create.title')}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.create.name')}</label>
            <input className="field-input" type="text" value={form.name}
              onChange={handleChange('name')} required autoFocus />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.create.slug')}</label>
            <input className="field-input" type="text" value={form.slug}
              onChange={handleChange('slug')} required pattern="[a-z0-9_-]+" />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.create.status')}</label>
            <select className="field-input" value={form.status} onChange={handleChange('status')}>
              <option value="trial">trial</option>
              <option value="active">active</option>
              <option value="demo">demo</option>
              <option value="test">test</option>
            </select>
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.create.tenant_type', 'Typ')}</label>
            <select className="field-input" value={form.tenant_type} onChange={handleChange('tenant_type')}>
              <option value="external">external</option>
              <option value="internal">internal</option>
            </select>
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.create.tier', 'Tarif')}</label>
            <select className="field-input" value={form.tier} onChange={handleChange('tier')}>
              <option value="basic">basic</option>
              <option value="professional">professional</option>
              <option value="enterprise">enterprise</option>
            </select>
          </div>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
              borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)',
              marginBottom: 12,
            }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('common.loading') : t('admin.tenants.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Create-User-Modal -------------------------------------------------------

function CreateUserModal({ onClose, onCreated, api, t, tenant }) {
  // user_type-Vorgabe aus dem Tenant ableiten (Backend leitet bei 'auto' selbst ab).
  const [form, setForm] = useState({
    email: '', username: '', password: '', display_name: '',
    platform_role: 'user', user_type: 'auto',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (key) => (e) => {
    const value = e.target.value;
    setForm(f => {
      const next = { ...f, [key]: value };
      // username aus dem lokalen Teil der E-Mail vorschlagen,
      // solange username leer oder bisher automatisch war.
      if (key === 'email' && (!f.username || f.username === localPart(f.email))) {
        next.username = localPart(value);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = {
        email: form.email.trim(),
        username: (form.username || form.email).trim(),
        password: form.password,
        display_name: form.display_name.trim() || undefined,
        platform_role: form.platform_role,
        platform_tenant_id: tenant.id,
      };
      // 'auto' -> Feld weglassen, damit das Backend user_type aus dem Tenant ableitet.
      if (form.user_type !== 'auto') body.user_type = form.user_type;
      await api('/admin/users', { method: 'POST', body });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, width: '90%', maxWidth: 480, padding: 28,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
          {t('admin.tenants.users.create.title', 'Neuen Benutzer anlegen')}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 18 }}>
          {t('admin.tenants.users.create.subtitle', 'Mandant')}: {tenant.name}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.users.create.email', 'E-Mail')}</label>
            <input className="field-input" type="email" value={form.email}
              onChange={handleChange('email')} required autoFocus />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.users.create.username', 'Benutzername')}</label>
            <input className="field-input" type="text" value={form.username}
              onChange={handleChange('username')} required />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.users.create.display_name', 'Anzeigename (optional)')}</label>
            <input className="field-input" type="text" value={form.display_name}
              onChange={handleChange('display_name')} />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.users.create.password', 'Passwort')}</label>
            <input className="field-input" type="password" value={form.password}
              onChange={handleChange('password')} required minLength={8}
              autoComplete="new-password" />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.users.create.role', 'Rolle')}</label>
            <select className="field-input" value={form.platform_role} onChange={handleChange('platform_role')}>
              <option value="user">user</option>
              <option value="org_admin">org_admin</option>
              <option value="platform_admin">platform_admin</option>
            </select>
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.users.create.user_type', 'Nutzertyp')}</label>
            <select className="field-input" value={form.user_type} onChange={handleChange('user_type')}>
              <option value="auto">{t('admin.tenants.users.create.user_type_auto', 'automatisch (aus Mandant)')}</option>
              <option value="external">external</option>
              <option value="internal">internal</option>
            </select>
          </div>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
              borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)',
              marginBottom: 12,
            }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('common.loading') : t('admin.tenants.users.create.submit', 'Benutzer anlegen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Edit-Tenant-Modal (auch interne Mandanten) ------------------------------

function EditTenantModal({ onClose, onUpdated, api, t, tenant }) {
  const [form, setForm] = useState({
    name: tenant.name || '',
    status: tenant.status || 'active',
    tenant_type: tenant.tenant_type || 'external',
    tier: tenant.tier || 'professional',
    contact_email: tenant.contact_email || '',
    legal_name: tenant.legal_name || '',
    vat_id: tenant.vat_id || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const change = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      // Nur gesetzte Felder senden; Slug ist unveraenderlich (nicht im TenantUpdate).
      const body = {
        name: form.name.trim(),
        status: form.status,
        tenant_type: form.tenant_type,
        tier: form.tier,
        contact_email: form.contact_email.trim() || null,
        legal_name: form.legal_name.trim() || null,
        vat_id: form.vat_id.trim() || null,
      };
      await api(`/admin/tenants/${tenant.id}`, { method: 'PUT', body });
      onUpdated();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, width: '90%', maxWidth: 480, padding: 28,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
          {t('admin.tenants.edit.title', 'Mandant bearbeiten')}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 18 }}>
          {tenant.slug} - {tenant.id}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.info.name')}</label>
            <input className="field-input" type="text" value={form.name}
              onChange={change('name')} required autoFocus />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.create.status')}</label>
            <select className="field-input" value={form.status} onChange={change('status')}>
              <option value="trial">trial</option>
              <option value="active">active</option>
              <option value="demo">demo</option>
              <option value="test">test</option>
              <option value="suspended">suspended</option>
            </select>
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.create.tenant_type', 'Typ')}</label>
            <select className="field-input" value={form.tenant_type} onChange={change('tenant_type')}>
              <option value="external">external</option>
              <option value="internal">internal</option>
            </select>
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.create.tier', 'Tarif')}</label>
            <select className="field-input" value={form.tier} onChange={change('tier')}>
              <option value="basic">basic</option>
              <option value="professional">professional</option>
              <option value="enterprise">enterprise</option>
            </select>
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.info.contact')}</label>
            <input className="field-input" type="email" value={form.contact_email}
              onChange={change('contact_email')} />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.edit.legal_name', 'Firmenname (rechtlich)')}</label>
            <input className="field-input" type="text" value={form.legal_name}
              onChange={change('legal_name')} />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.edit.vat_id', 'USt-IdNr.')}</label>
            <input className="field-input" type="text" value={form.vat_id}
              onChange={change('vat_id')} />
          </div>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
              borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)',
              marginBottom: 12,
            }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save', 'Speichern')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Edit-User-Modal (Rolle/Status + PRISM-OS-Zugriff) -----------------------

function EditUserModal({ onClose, onUpdated, api, t, user }) {
  const [form, setForm] = useState({
    platform_role: user.platform_role || user.role || 'user',
    is_active: user.is_active !== false,
  });
  const [detail, setDetail] = useState(null);   // GET /admin/users/{id} (prism_os_access)
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Passwort setzen (Admin-Reset ueber bestehenden /users/{id}/reset-password-Endpunkt).
  const [pw, setPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await api(`/admin/users/${user.id}`);
        if (alive) {
          setDetail(d);
          setForm({
            platform_role: d.platform_role || 'user',
            is_active: d.is_active !== false,
          });
        }
      } catch {
        // Detail ist optional (nur Anzeige des OS-Zugriffs) - Edit bleibt moeglich.
      }
    })();
    return () => { alive = false; };
  }, [api, user.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api(`/admin/users/${user.id}`, {
        method: 'PUT',
        body: { platform_role: form.platform_role, is_active: form.is_active },
      });
      onUpdated();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  // Passwort als Admin setzen/zuruecksetzen. Das Passwort wird nur hier eingegeben,
  // per POST an das Backend uebergeben und nirgends geloggt. Der Nutzer sollte es
  // beim ersten Login aendern.
  const setPassword = async (e) => {
    e.preventDefault();
    setPwMsg('');
    if (!pw || pw.length < 8) {
      setPwMsg(t('admin.tenants.users.edit.pw_too_short', 'Passwort: mindestens 8 Zeichen'));
      return;
    }
    setPwSaving(true);
    try {
      await api(`/admin/users/${user.id}/reset-password`, {
        method: 'POST', body: { new_password: pw },
      });
      setPw('');
      setPwMsg(t('admin.tenants.users.edit.pw_set_ok', 'Passwort gesetzt. Bitte dem Nutzer sicher mitteilen.'));
    } catch (err) {
      setPwMsg(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''));
    } finally {
      setPwSaving(false);
    }
  };

  const osa = detail?.prism_os_access;
  const osText = !osa ? t('common.loading', 'Lade...')
    : osa.allowed
      ? t('admin.tenants.users.edit.os_yes', 'Ja')
        + ` (${osa.role}, ${osa.source === 'implicit'
            ? t('admin.tenants.users.edit.os_implicit', 'automatisch: interner Nutzer')
            : t('admin.tenants.users.edit.os_explicit', 'explizit zugewiesen')})`
      : t('admin.tenants.users.edit.os_no', 'Nein');

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, width: '90%', maxWidth: 480, padding: 28,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
          {t('admin.tenants.users.edit.title', 'Benutzer bearbeiten')}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 18 }}>
          {user.display_name || user.username} - {user.email}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="field-label">{t('admin.tenants.users.create.role', 'Rolle')}</label>
            <select className="field-input" value={form.platform_role}
              onChange={(e) => setForm((f) => ({ ...f, platform_role: e.target.value }))}>
              <option value="user">user</option>
              <option value="org_admin">org_admin</option>
              <option value="platform_admin">platform_admin</option>
            </select>
          </div>
          <div className="form-group">
            <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
              {t('admin.tenants.users.edit.active', 'Aktiv')}
            </label>
          </div>
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)',
            marginBottom: 12,
          }}>
            <strong style={{ color: 'var(--text-primary)' }}>
              {t('admin.tenants.users.edit.os_access', 'PRISM-OS-Zugriff')}:
            </strong>{' '}{osText}
            <div style={{ marginTop: 4 }}>
              {t('admin.tenants.users.edit.os_hint',
                 'Interne Nutzer (user_type=internal) erhalten PRISM-OS-Zugriff automatisch. Der Nutzertyp wird aus dem Mandanten abgeleitet.')}
            </div>
          </div>
          {/* Passwort setzen (eigene Aktion; kein verschachteltes form) */}
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '10px 12px', marginBottom: 12,
          }}>
            <label className="field-label">
              {t('admin.tenants.users.edit.set_pw', 'Passwort setzen / zuruecksetzen')}
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <input className="field-input" type="password" value={pw} minLength={8}
                autoComplete="new-password" style={{ flex: 1 }}
                placeholder={t('admin.tenants.users.edit.new_pw', 'Neues Passwort (min. 8 Zeichen)')}
                onChange={(e) => setPw(e.target.value)} />
              <button type="button" className="btn btn-ghost" onClick={setPassword} disabled={pwSaving}>
                {pwSaving ? t('common.loading', 'Lade...') : t('admin.tenants.users.edit.set_pw_btn', 'Setzen')}
              </button>
            </div>
            {pwMsg && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{pwMsg}</div>
            )}
          </div>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
              borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)',
              marginBottom: 12,
            }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save', 'Speichern')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function localPart(email) {
  return (email || '').split('@')[0].toLowerCase().trim();
}

function slugify(s) {
  return (s || '').toLowerCase()
    .replace(/[\u00e4\u00c4]/g, 'ae').replace(/[\u00f6\u00d6]/g, 'oe')
    .replace(/[\u00fc\u00dc]/g, 'ue').replace(/[\u00df]/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);
}
